import { Printer, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import {
	useCommitmentMatrixDashboard,
	useCommitmentMatrixEvolution,
} from '@/api/queries/commitment-matrix-queries'
import { BranchBreakdownCard } from '@/components/administration/commitment-matrix/dashboard/branch-breakdown-card'
import { CollaboratorsEvolutionTable } from '@/components/administration/commitment-matrix/dashboard/collaborators-evolution-table'
import { CollaboratorsPrintModal } from '@/components/administration/commitment-matrix/dashboard/collaborators-print-modal'
import { CollaboratorsTable } from '@/components/administration/commitment-matrix/dashboard/collaborators-table'
import { KpiCards } from '@/components/administration/commitment-matrix/dashboard/kpi-cards'
import { PersonTimelineModal } from '@/components/administration/commitment-matrix/dashboard/person-timeline-modal'
import {
	nextSort,
	type SortState,
} from '@/components/administration/commitment-matrix/dashboard/sortable-head'
import { SearchInput } from '@/components/commons'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Toggle } from '@/components/ui/toggle'
import type {
	CommitmentMatrixDashboardSortColumn,
	CommitmentMatrixEvolutionSortColumn,
	DateRange,
} from '@/types'

interface CommitmentMatrixDashboardProps {
	branchIds?: string[]
	crIds?: string[]
	jobGroupClusterId?: string
	jobGroupId?: string
	range: DateRange
}

const PAGE_SIZE = 50
const DEFAULT_SORT: SortState<CommitmentMatrixDashboardSortColumn> = {
	column: 'target',
	direction: 'desc',
}
const DEFAULT_EVOLUTION_SORT: SortState<CommitmentMatrixEvolutionSortColumn> = {
	column: 'name',
	direction: 'asc',
}

export function CommitmentMatrixDashboard({
	branchIds,
	crIds,
	jobGroupClusterId,
	jobGroupId,
	range,
}: CommitmentMatrixDashboardProps) {
	const [badge, setBadge] = useState('')
	const [name, setName] = useState('')
	const [page, setPage] = useState(1)
	const [sort, setSort] = useState(DEFAULT_SORT)
	const [evolutionSort, setEvolutionSort] = useState(DEFAULT_EVOLUTION_SORT)
	const [showEvolution, setShowEvolution] = useState(false)
	const [printOpen, setPrintOpen] = useState(false)
	const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null)

	const scopeParams = {
		branchIds: branchIds?.length ? branchIds.join(',') : undefined,
		crIds: crIds?.length ? crIds.join(',') : undefined,
		jobGroupClusterId,
		jobGroupId: jobGroupClusterId ? undefined : jobGroupId,
		badge: badge || undefined,
		name: name || undefined,
		rangeStart: range.start.toISOString().slice(0, 10),
		rangeEnd: range.end.toISOString().slice(0, 10),
		page,
		pageSize: PAGE_SIZE,
	}
	const params = {
		...scopeParams,
		sortBy: sort.column,
		sortDir: sort.direction,
	}

	const { data, isLoading, isFetching, isPlaceholderData } =
		useCommitmentMatrixDashboard(params)
	const evolution = useCommitmentMatrixEvolution(
		{
			...scopeParams,
			sortBy: evolutionSort.column,
			sortDir: evolutionSort.direction,
		},
		showEvolution,
	)

	const handleBadgeChange = (value: string) => {
		setBadge(value)
		setPage(1)
	}
	const handleNameChange = (value: string) => {
		setName(value)
		setPage(1)
	}

	if (isLoading || !data) {
		return <CommitmentMatrixDashboardLoading />
	}

	const {
		metrics = {
			totalPeopleInScope: 0,
			totalTargetInspections: 0,
			totalDoneInspections: 0,
			totalVereAgirCount: 0,
			totalDirecionadaCount: 0,
			totalInteracaoCount: 0,
			totalCartaoAmareloCount: 0,
			completionPct: 0,
			engagementPct: 0,
			onTrackPct: 0,
		},
		branchBreakdown = [],
		collaborators,
	} = data
	const activeCollaborators = showEvolution
		? evolution.data?.collaborators
		: collaborators
	const {
		items: activeItems = [],
		page: currentPage = page,
		total = 0,
		totalPages = 0,
	} = activeCollaborators ?? {}
	const activeIsPlaceholderData = showEvolution
		? evolution.isPlaceholderData
		: isPlaceholderData

	const handleSort = (column: CommitmentMatrixDashboardSortColumn) => {
		setSort((current) => nextSort(current, column))
		setPage(1)
	}
	const handleEvolutionSort = (column: CommitmentMatrixEvolutionSortColumn) => {
		setEvolutionSort((current) => nextSort(current, column))
		setPage(1)
	}
	const handleToggleEvolution = (pressed: boolean) => {
		setShowEvolution(pressed)
		setPage(1)
	}

	return (
		<div className="flex flex-col gap-4">
			<KpiCards metrics={metrics} />

			<BranchBreakdownCard branchBreakdown={branchBreakdown} />

			{/* Filters */}
			<div className="flex flex-col gap-3 sm:flex-row">
				<SearchInput
					value={badge}
					onChange={handleBadgeChange}
					placeholder="Buscar matrícula..."
					className="sm:w-48"
				/>
				<SearchInput
					value={name}
					onChange={handleNameChange}
					placeholder="Buscar nome..."
					className="sm:flex-1"
				/>
				<Toggle
					variant="outline"
					size="sm"
					className="gap-1.5 px-3"
					pressed={showEvolution}
					onPressedChange={handleToggleEvolution}
					aria-label="Mostrar evolução mensal"
				>
					<TrendingUp className="size-4" />
					Evolução
				</Toggle>
				{!showEvolution && (
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="gap-1.5"
						onClick={() => setPrintOpen(true)}
					>
						<Printer className="size-4" />
						Baixar PDF
					</Button>
				)}
			</div>

			{!showEvolution ? (
				<CollaboratorsTable
					items={collaborators?.items ?? []}
					isFetching={isFetching}
					sort={sort}
					onSort={handleSort}
					onSelectPerson={setSelectedPersonId}
				/>
			) : evolution.isError ? (
				<p className="rounded-lg border border-kaefer-light-silver bg-white py-10 text-center text-sm text-muted-foreground">
					Não foi possível carregar a evolução. Verifique se o período tem no
					máximo 12 meses.
				</p>
			) : !evolution.data ? (
				<Skeleton className="h-64 w-full rounded-lg" />
			) : (
				<CollaboratorsEvolutionTable
					months={evolution.data.months}
					items={evolution.data.collaborators.items}
					scopeTotal={evolution.data.scopeTotal}
					isFetching={evolution.isFetching}
					sort={evolutionSort}
					onSort={handleEvolutionSort}
					onSelectPerson={setSelectedPersonId}
				/>
			)}

			<CollaboratorsPrintModal
				open={printOpen}
				onClose={() => setPrintOpen(false)}
				params={params}
			/>

			<PersonTimelineModal
				personId={selectedPersonId}
				range={range}
				onClose={() => setSelectedPersonId(null)}
			/>

			{total > 0 && (
				<div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
					<span>
						Mostrando{' '}
						{activeItems.length > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0}–
						{(currentPage - 1) * PAGE_SIZE + activeItems.length} de {total} ·
						página {currentPage} de {totalPages}
					</span>
					<div className="flex gap-2">
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={currentPage <= 1 || activeIsPlaceholderData}
							onClick={() => setPage((p) => Math.max(1, p - 1))}
						>
							‹ Anterior
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={currentPage >= totalPages || activeIsPlaceholderData}
							onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						>
							Próxima ›
						</Button>
					</div>
				</div>
			)}
		</div>
	)
}

export function CommitmentMatrixDashboardLoading() {
	return (
		<div className="flex flex-col gap-4">
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
				{['a', 'b', 'c', 'd', 'e', 'f', 'g'].map((key) => (
					<div
						key={key}
						className="rounded-lg border border-kaefer-light-silver bg-white p-4"
					>
						<Skeleton className="h-7 w-16" />
						<Skeleton className="mt-2 h-3.5 w-20" />
					</div>
				))}
			</div>
			<Skeleton className="h-10 w-full" />
			<Skeleton className="h-64 w-full rounded-lg" />
			<Skeleton className="h-56 w-full rounded-lg" />
		</div>
	)
}
